import { get } from 'lodash';
import { call, all, takeEvery, put, select } from 'redux-saga/effects';
import EventNames from 'src/lib/eventNames';
import userDux from 'src/redux/user';
import appDux from 'src/redux/app';
import utils from 'src/utils';
import trackingDux from './index';

const { generateTimestamp } = utils.metrics;

/**
 * Returns additional information from the user and job to be appended
 * to every tracking event inside of viewer.
 * @export
 * @param {Object} properties additional information to pass to tracking service
 * @param {Object} user
 * @param {Object} job
 * @returns {Object} updated properties object
 */
export function* getHydratedEventProps(properties) {
  const commonMetricData = yield select(
    trackingDux.selectors.getCommonMetricData,
  );

  return {
    ...commonMetricData,
    ...properties,
  };
}

/**
 * trackingSaga handles all tracking events from viewer and appends necessary
 * global properties for tracking purposes
 * @export
 * @param {Object} action action
 * @param {string} action.type action type
 * @param {Object} action.payload action.payload
 * @param {string} action.payload.eventName eventname (ie name in analytics)
 * @param {Object} action.payload.properties additional properties to pass to tracking service
 */
export function* trackingSaga(action) {
  if (!window.analytics) return;

  const { eventName, properties } = action.payload;
  const hydratedProps = yield call(getHydratedEventProps, properties);
  const timestamp = yield call(generateTimestamp);

  window.analytics.track(eventName, hydratedProps, timestamp);
}

/**
 * identifyUserSaga handles all identify events from viewer and appends
 * necessary global properties for tracking purposes
 * @export
 * @param {Object} action action
 * @param {string} action.type action type, probably viewer track or time event
 * @param {Object} action.payload action.payload
 * @param {Object} action.payload.properties additional properties to pass to tracking service
 */
export function* identifyUserSaga(action) {
  if (!window.analytics) return;

  const { properties } = action.payload;
  const hydratedProps = yield call(getHydratedEventProps, properties);
  const timestamp = yield call(generateTimestamp);
  const { user_id } = hydratedProps;

  window.analytics.page();

  if (!user_id) {
    window.analytics.identify(hydratedProps, timestamp);
  } else {
    window.analytics.identify(user_id, hydratedProps, timestamp);
  }
}

export function* trackAttributeSelectionSaga(action) {
  const { attributeProperty, elementStyle } = action.payload;
  const { nodes, ...trackingProperties } = elementStyle;
  const { track } = trackingDux.actions;
  const {
    DESIGN_FEATURE_COLOR_SELECTION,
    DESIGN_FEATURE_MANUFACTURER_SELECTION,
    DESIGN_FEATURE_MATERIAL_SELECTION,
    DESIGN_FEATURE_PAINT_BRAND_SELECTION,
    DESIGN_FEATURE_PAINT_COLOR_SELECTION,
    DESIGN_FEATURE_PRODUCT_SELECTION,
  } = EventNames.viewer;

  switch (attributeProperty) {
    case 'materialId':
      yield put(track(DESIGN_FEATURE_MATERIAL_SELECTION, trackingProperties));
      break;
    case 'brandId':
      yield put(
        track(DESIGN_FEATURE_MANUFACTURER_SELECTION, trackingProperties),
      );
      break;
    case 'productId':
      yield put(track(DESIGN_FEATURE_PRODUCT_SELECTION, trackingProperties));
      break;
    case 'colorId':
      yield put(track(DESIGN_FEATURE_COLOR_SELECTION, trackingProperties));
      break;
    case 'paintBrandId':
      yield put(
        track(DESIGN_FEATURE_PAINT_BRAND_SELECTION, trackingProperties),
      );
      break;
    case 'paintColorId':
      yield put(
        track(DESIGN_FEATURE_PAINT_COLOR_SELECTION, trackingProperties),
      );
      break;
    default:
      break;
  }
}

export default function* sagas() {
  yield all([
    takeEvery(trackingDux.actions.track, trackingSaga),
    takeEvery(trackingDux.actions.identify, identifyUserSaga),
    takeEvery(
      trackingDux.actions.trackAttributeSelection,
      trackAttributeSelectionSaga,
    ),
  ]);
}
